import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { useAuthenticatedAxios } from "."
import { useHasSession } from "core/hooks/use-session-key"
import type { WorkspacesListResponse } from "@seamapi/http"

type InternalWorkspaceListEntry =
  WorkspacesListResponse["workspaces"][number] & {
    webview_inviter_logo_url: string | null
    webview_logo_shape: string | null
    webview_primary_button_color: string | null
    webview_primary_button_text_color: string | null
    webview_success_message: string | null
    suspended: boolean
    // Present on the internal endpoint; not in the public Workspace type.
    // Optional so older API versions that don't return it stay typed.
    created_at?: string
  }

export const useWorkspaces = () => {
  const axios = useAuthenticatedAxios()
  const hasSession = useHasSession()
  return useQuery({
    queryKey: ["workspaces"],
    queryFn: () =>
      axios
        .get("/workspaces/list")
        .then((r) => r.data.workspaces as InternalWorkspaceListEntry[]),
    // Pages mount this before useEnsureSeamSession has exchanged the Clerk
    // token for a Seam session; wait for it instead of sending Bearer null.
    enabled: hasSession,
  })
}

/**
 * Resolves the id of the organization that owns a workspace, straight from the
 * `/workspaces/list` payload. Works for workspaces the user can access without
 * being a member of the owning organization.
 */
export const useWorkspaceOrganizationId = (
  workspaceId: string | null | undefined
): { organizationId: string | null; isLoading: boolean } => {
  const { data: workspaces = [], isLoading } = useWorkspaces()

  const organizationId = useMemo(() => {
    if (workspaceId == null) return null
    return (
      workspaces.find((workspace) => workspace.workspace_id === workspaceId)
        ?.organization_id ?? null
    )
  }, [workspaces, workspaceId])

  return { organizationId, isLoading }
}
